import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api';

// 60s baseline poll; job completions additionally trigger an immediate
// refetch via the 'autonoma:refresh-notifications' window event (fired
// by JobProgressPill the moment a job flips to complete).
const POLL_MS = 60 * 1000;

/**
 * useNotifications — read + mutate the in-app notification feed for a shop.
 *
 * Polls /api/notifications every 5 minutes (cancellable on unmount and
 * when shop changes). Optimistically updates local state on read/action
 * mutations so the bell counter feels instant; the server is the source
 * of truth and `refetch()` reconciles if the user wants to force-sync.
 *
 * Returns:
 *   {
 *     notifications:  Array,        // newest first
 *     unreadCount:    number,
 *     loading:        boolean,
 *     markRead(id),
 *     markActioned(id),
 *     refetch(),
 *   }
 */
export default function useNotifications(shop) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  // Guards against stale shop responses overwriting the current shop's data.
  const activeShopRef = useRef(shop);

  const fetchNotifications = useCallback(async () => {
    if (!shop) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    activeShopRef.current = shop;
    try {
      const res = await apiFetch(
        `/api/notifications`,
        { credentials: 'include' },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      // Drop the response if the active shop changed mid-flight.
      if (activeShopRef.current !== shop) return;
      if (data.success) {
        setNotifications(Array.isArray(data.notifications) ? data.notifications : []);
        setUnreadCount(Number(data.unreadCount) || 0);
      }
    } catch (err) {
      console.error('Notifications fetch error:', err);
    } finally {
      if (activeShopRef.current === shop) setLoading(false);
    }
  }, [shop]);

  const markRead = useCallback(async (id) => {
    if (!shop || !id) return;
    setNotifications(prev => prev.map(n => (n.id === id ? { ...n, read: true } : n)));
    setUnreadCount(prev => Math.max(0, prev - 1));
    try {
      await apiFetch(
        `/api/notifications/${encodeURIComponent(id)}/read`,
        { method: 'PUT', credentials: 'include' },
      );
    } catch (err) {
      console.error('markRead error:', err);
    }
  }, [shop]);

  const markActioned = useCallback(async (id) => {
    if (!shop || !id) return;
    setNotifications(prev => prev.map(n => (
      n.id === id ? { ...n, actioned: true, read: true } : n
    )));
    setUnreadCount(prev => Math.max(0, prev - 1));
    try {
      await apiFetch(
        `/api/notifications/${encodeURIComponent(id)}/action`,
        { method: 'PUT', credentials: 'include' },
      );
    } catch (err) {
      console.error('markActioned error:', err);
    }
  }, [shop]);

  // Local-only flag update — for flows whose server call already
  // recorded the state change (the review card's 👍 👎 / "Not now" go
  // through /api/review-prompt/respond, which marks the notification
  // itself; calling PUT /read as well would double-count a dismissal).
  const applyLocal = useCallback((id, patch) => {
    if (!id) return;
    setNotifications(prev => {
      const before = prev.find(n => n.id === id);
      if (before && !before.read && patch.read) setUnreadCount(c => Math.max(0, c - 1));
      return prev.map(n => (n.id === id ? { ...n, ...patch } : n));
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchNotifications();
    const interval = setInterval(fetchNotifications, POLL_MS);
    // Instant refresh the moment a background job completes (fired by
    // JobProgressPill) — the completion notification appears in the
    // bell right away, not on the next poll.
    const onJobComplete = () => fetchNotifications();
    window.addEventListener('autonoma:refresh-notifications', onJobComplete);
    return () => {
      clearInterval(interval);
      window.removeEventListener('autonoma:refresh-notifications', onJobComplete);
    };
  }, [fetchNotifications]);

  return {
    notifications,
    unreadCount,
    loading,
    markRead,
    markActioned,
    applyLocal,
    refetch: fetchNotifications,
  };
}
